import { datadogLogs } from "@datadog/browser-logs";
import type { LogLayerTransportParams, LogLevel } from "@loglayer/transport";
import { DataDogBrowserLogsTransport } from "@loglayer/transport-datadog-browser-logs";
import { LogLayer, ConsoleTransport } from "loglayer";

import { getBrowserEnvVar, ENABLE_ANALYTICS } from "./env.shared";
import { redactLogTransportParams } from "./logger-redaction";

// Custom transport wrapper to filter messages
class FilteredConsoleTransport extends ConsoleTransport {
    // AIDEV-NOTE: Override shipToLogger to filter out "viewed screen" messages
    // Using 'any' return type to match parent class signature variance
    shipToLogger({
        logLevel,
        messages,
        data,
        hasData,
    }: LogLayerTransportParams): any[] {
        // AIDEV-NOTE: Confirmed this is reached; ensure console level shows these
        // Filter out "viewed screen" messages
        if (
            typeof messages[0] === "string" &&
            messages[0].includes("viewed screen")
        ) {
            return [];
        }
        // AIDEV-NOTE: Cast logLevel because LogLayerTransportParams uses LogLevelType (includes "trace")
        // but parent ConsoleTransport expects LogLevel (subset without "trace")
        return super.shipToLogger({
            logLevel: logLevel as LogLevel,
            messages,
            data,
            hasData,
        });
    }
}

class FilteredDataDogTransport extends DataDogBrowserLogsTransport {
    // AIDEV-NOTE: Use shipToLogger like other transports; filter noisy events
    shipToLogger({
        logLevel,
        messages,
        data,
        hasData,
    }: LogLayerTransportParams): unknown[] {
        // AIDEV-NOTE: Limit Datadog logs to info+ to avoid debug/trace noise.
        if (logLevel === "debug" || logLevel === "trace") {
            return [];
        }
        if (typeof messages[0] !== "string") {
            return super.shipToLogger(
                redactLogTransportParams({
                    logLevel,
                    messages,
                    data,
                    hasData,
                }),
            );
        }

        const message = messages[0];
        if (
            message.includes("viewed screen") ||
            message.includes("Fetch error GET") ||
            message.includes("Failed to fetch dynamically imported module")
        ) {
            return [];
        }
        // AIDEV-NOTE: Keep warn/error "Failed to fetch" signals; only suppress lower-severity noise.
        if (message.includes("Failed to fetch") && logLevel === "info") {
            return [];
        }
        // AIDEV-NOTE: HIPAA — browser logs bypass server transports, so scrub
        // clinical keys and identifiers immediately before Datadog egress.
        return super.shipToLogger(
            redactLogTransportParams({ logLevel, messages, data, hasData }),
        );
    }
}

// AIDEV-NOTE: Only add DataDog transport when analytics is enabled (not in test/ci environments)
const baseLogger = new LogLayer({
    transport: [
        new FilteredConsoleTransport({
            logger: console,
        }),
        ...(ENABLE_ANALYTICS
            ? [
                  new FilteredDataDogTransport({
                      logger: datadogLogs.logger,
                  }),
              ]
            : []),
    ],
});

// AIDEV-NOTE: withContext MUTATES in place (loglayer v6). This browser singleton is
// single-user per tab, so identity writes (analytics.ts) are safe here — never
// import this in server code for per-request context.
export const logger = baseLogger.withContext({
    service: "app",
    env: getBrowserEnvVar("ENV_NAME"),
});
